import { Injectable } from '@angular/core';
import { ConnectionService } from './connection-service';

@Injectable({
  providedIn: 'root'
})
export class ExternalResourcesService {
  private loadedResources: Set<string> = new Set();

  constructor(private connectionService: ConnectionService) {}

  /**
   * Charge une ressource externe (script ou CSS) de manière différée
   * @param url URL de la ressource
   * @param type Type de ressource ('script' ou 'style')
   * @param isEssential Indique si la ressource est essentielle ou peut être omise sur connexion lente
   * @returns Promise qui se résout quand la ressource est chargée
   */
  loadExternalResource(url: string, type: 'script' | 'style', isEssential: boolean = false): Promise<void> {
    // Si la ressource a déjà été chargée, on ne la recharge pas
    if (this.loadedResources.has(url)) {
      return Promise.resolve();
    }

    // Si connexion lente et ressource non essentielle, ne pas charger
    if (this.connectionService.isSlowConnection() && !isEssential) {
      console.log(`Ressource non essentielle ignorée sur connexion lente: ${url}`);
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      let element: HTMLElement;

      if (type === 'script') {
        element = document.createElement('script');
        (element as HTMLScriptElement).src = url;
        (element as HTMLScriptElement).async = true;
        element.onload = () => {
          this.loadedResources.add(url);
          resolve();
        };
        element.onerror = (error) => reject(error);
      } else if (type === 'style') {
        element = document.createElement('link');
        (element as HTMLLinkElement).rel = 'stylesheet';
        (element as HTMLLinkElement).href = url;
        element.onload = () => {
          this.loadedResources.add(url);
          resolve();
        };
        element.onerror = (error) => reject(error);
      } else {
        reject(new Error(`Type de ressource non pris en charge: ${type}`));
        return;
      }

      document.head.appendChild(element);
    });
  }

  /**
   * Charge un script externe de manière différée
   * @param url URL du script
   * @param isEssential Indique si le script est essentiel ou peut être omis sur connexion lente
   * @returns Promise qui se résout quand le script est chargé
   */
  loadScript(url: string, isEssential: boolean = false): Promise<void> {
    return this.loadExternalResource(url, 'script', isEssential);
  }

  /**
   * Charge une feuille de style externe de manière différée
   * @param url URL de la feuille de style
   * @param isEssential Indique si la feuille de style est essentielle ou peut être omise sur connexion lente
   * @returns Promise qui se résout quand la feuille de style est chargée
   */
  loadStylesheet(url: string, isEssential: boolean = false): Promise<void> {
    return this.loadExternalResource(url, 'style', isEssential);
  }
}
